import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Mail, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_intelligence-rebuilt/artifacts/jhoeqook_ProcureAide%20Logo%20with%20Ai.png";

const footerLinks = {
  platform: [
    { name: "Overview", path: "/platform" },
    { name: "How It Works", path: "/how-it-works" },
    { name: "Industries", path: "/industries" },
  ],
  products: [
    { name: "DataRefinery", path: "/products/data-refinery" },
    { name: "SpendLens", path: "/products/spendlens" },
    { name: "ConTrack", path: "/products/contrack" },
  ],
  company: [
    { name: "About", path: "/about" },
    { name: "Insights", path: "/insights" },
    { name: "Contact", path: "/contact" },
    { name: "Community", path: "/community" },
    { name: "Advisory & Implementation", path: "/contact" },
  ],
};

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Thank you for subscribing!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("You're already subscribed!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#0B1426] border-t border-[#00B4D8]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <div className="bg-white rounded-lg px-3 py-1.5">
                <img 
                  src={LOGO_URL} 
                  alt="Procureaide" 
                  className="h-8 w-auto"
                />
              </div>
            </Link>
            <p className="text-[#94A3B8] text-sm leading-relaxed max-w-sm mb-6">
              Empowering your procurement potential with AI-driven intelligence. 
              Restructuring procurement by embedding intelligence into every decision.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#111F36] flex items-center justify-center text-[#94A3B8] hover:text-[#00B4D8] hover:bg-[#0A2463] transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#111F36] flex items-center justify-center text-[#94A3B8] hover:text-[#00B4D8] hover:bg-[#0A2463] transition-colors"
                data-testid="footer-twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href="mailto:info@procureaide.com"
                className="w-10 h-10 rounded-full bg-[#111F36] flex items-center justify-center text-[#94A3B8] hover:text-[#00B4D8] hover:bg-[#0A2463] transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Platform */}
          <div>
            <h4 className="text-[#F8FAFC] font-semibold mb-4">Platform</h4>
            <ul className="space-y-3">
              {footerLinks.platform.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-[#94A3B8] text-sm hover:text-[#00B4D8] transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-[#F8FAFC] font-semibold mb-4">Products</h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-[#94A3B8] text-sm hover:text-[#00B4D8] transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-[#F8FAFC] font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-[#94A3B8] text-sm hover:text-[#00B4D8] transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="mt-12 pt-8 border-t border-[#00B4D8]/10">
          <div className="max-w-md">
            <h4 className="text-[#F8FAFC] font-semibold mb-2">Stay Updated</h4>
            <p className="text-[#94A3B8] text-sm mb-4">
              Subscribe to receive the latest procurement AI insights.
            </p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC] placeholder:text-[#64748B]"
                data-testid="footer-newsletter-email"
              />
              <Button 
                type="submit"
                disabled={loading}
                className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4]"
                data-testid="footer-newsletter-submit"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-[#00B4D8]/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#64748B] text-sm">
            © {new Date().getFullYear()} Procureaide. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-[#64748B] text-sm hover:text-[#00B4D8] transition-colors" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-[#64748B] text-sm hover:text-[#00B4D8] transition-colors" data-testid="footer-terms-link">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
